/**
 * The assistant's example copy, built around the account's own location names.
 *
 * Every "for example…" string the assistant shows used to name a hardcoded
 * location. That reads as a bug to anyone who does not happen to own a location
 * by that name — which is everyone except the developer whose local data it came
 * from. Worse, it invites the user to type an instruction naming a location the
 * assistant will then refuse to resolve, so the first thing they try fails.
 *
 * Examples therefore either name a location the account actually owns, or name
 * none at all. There is no third option: a placeholder like "your location" is
 * not something a user can usefully paste back.
 */

import type { LocationStatus } from '../../types/LocationStatus';

export interface ExampleLocation {
  id: string;
  status: LocationStatus;
  locationName?: string;
}

export interface BuildExamplesOptions {
  /**
   * Questions this account can actually get an answer to, in registry order.
   * Passed in rather than read from the knowledge registry directly so this
   * module stays free of the request layer the registry pulls in.
   */
  questionExamples?: string[];
  /** Whether to offer a starter that makes a change. */
  canEditLocations?: boolean;
}

export interface AssistantExamples {
  /** A location name safe to use in examples, or null when there is none. */
  sampleName: string | null;
  /** Short prompts offered as starter chips. */
  starters: string[];
  /** The "I could not understand that" help text. */
  unknownHelp: string;
  /**
   * Concrete changes this account could ask for, already naming one of its own
   * locations. Shared with {@link unknownHelp} so the two cannot drift.
   */
  editExamples: string[];
}

/**
 * Longest name that still reads as an example rather than swamping it. Beyond
 * this the generic phrasing is clearer than a chip that wraps to three lines.
 */
const MAX_SAMPLE_NAME_LENGTH = 28;

/** Keeps the chip strip to one or two rows alongside the edit starter. */
const MAX_QUESTION_STARTERS = 2;

/**
 * Used when no caller supplies the account's answerable questions — matches the
 * supplier registry, which is the only audience with skills today.
 */
const DEFAULT_QUESTION_STARTERS = [
  'how many bookings do I have right now',
  'what was my total revenue last month',
];

/**
 * Chips are UI copy, but the questions they come from are stored as the user
 * would type them (lowercase), because they are also quoted back mid-sentence.
 * Capitalising here keeps both readings correct without duplicating the strings.
 */
const asChip = (question: string): string =>
  question.charAt(0).toUpperCase() + question.slice(1);

/**
 * Picks the location to build examples around.
 *
 * Prefers an active one: an example naming a draft location invites an edit the
 * user is less likely to want, and a draft may be a half-finished throwaway with
 * a placeholder name. Falls back to any location so an account whose listings
 * are all pending still gets concrete examples. Order follows the caller's list
 * so the copy is stable between renders rather than shuffling.
 */
export const pickSampleName = (locations: ExampleLocation[] = []): string | null => {
  const usable = locations.filter(
    (location) =>
      typeof location.locationName === 'string' &&
      location.locationName.trim().length > 0 &&
      location.locationName.trim().length <= MAX_SAMPLE_NAME_LENGTH,
  );

  const chosen = usable.find((location) => location.status === 'active') ?? usable[0];
  return chosen?.locationName?.trim() ?? null;
};

/**
 * Builds the example copy for an account.
 *
 * The scoped examples ("all my active locations") never need a name, so they
 * stay identical in both branches — only the ones that must address a single
 * location change shape.
 */
export const buildExamples = (
  locations: ExampleLocation[] = [],
  options: BuildExamplesOptions = {},
): AssistantExamples => {
  const sampleName = pickSampleName(locations);
  const {
    questionExamples = DEFAULT_QUESTION_STARTERS,
    canEditLocations = true,
  } = options;

  /*
   * Chips are the first thing a user clicks, so every one of them has to work.
   * An account with no write access is not offered an edit, and an account with
   * no answerable questions is not offered any — an empty strip is better than
   * a prompt that comes back refused.
   */
  const buildStarters = (editStarter: string): string[] => [
    ...questionExamples.slice(0, MAX_QUESTION_STARTERS).map(asChip),
    ...(canEditLocations ? [editStarter] : []),
  ];

  if (!sampleName) {
    const editExamples = [
      'set the capacity to 1433',
      'turn off overages on all my active locations',
      'update the description to make sure they ask for Bobby at the gate',
    ];
    return {
      sampleName: null,
      starters: buildStarters('Turn off overages on all my active locations'),
      editExamples,
      unknownHelp:
        'I can update your locations \u2014 name the location and what to change. For example: ' +
        `${editExamples.map((example) => `"${example}"`).join(', ')}. ` +
        'If you do not name a location I will ask which one you mean.',
    };
  }

  const editExamples = [
    `set the capacity on ${sampleName} to 1433`,
    `turn off overages for ${sampleName}`,
    `update the description on ${sampleName} to make sure they ask for Bobby at the gate`,
  ];

  return {
    sampleName,
    starters: buildStarters(`Turn off overages on ${sampleName}`),
    editExamples,
    unknownHelp:
      'I can update your locations \u2014 name the location and what to change. For example: ' +
      `${editExamples.map((example) => `"${example}"`).join(', ')}.`,
  };
};
